import { API_URL } from '@/lib/api';
import type { Group, GroupInvite, GroupType, MemberRole, DogOwner, Visibility } from '@/types/group';
import type { Trail } from '@/types/trail';

const auth = (token: string) => ({ Authorization: `Bearer ${token}` });
const j = (token: string) => ({ 'Content-Type': 'application/json', ...auth(token) });
const err = async (r: Response) => {
  const e = await r.json().catch(() => ({}));
  throw new Error(e.detail || `Request failed (${r.status})`);
};

// ── groups ──
export async function createGroup(token: string, name: string, type: GroupType): Promise<Group> {
  const r = await fetch(`${API_URL}/groups`, { method: 'POST', headers: j(token), body: JSON.stringify({ name, type }) });
  if (!r.ok) return err(r);
  return r.json();
}
export async function listMyGroups(token: string, type?: GroupType): Promise<Group[]> {
  const q = type ? `?type=${type}` : '';
  const r = await fetch(`${API_URL}/groups${q}`, { headers: auth(token) });
  if (!r.ok) return err(r);
  return r.json();
}
export async function getGroup(token: string, groupId: string): Promise<Group> {
  const r = await fetch(`${API_URL}/groups/${groupId}`, { headers: auth(token) });
  if (!r.ok) return err(r);
  return r.json();
}
export async function deleteGroup(token: string, groupId: string): Promise<void> {
  const r = await fetch(`${API_URL}/groups/${groupId}`, { method: 'DELETE', headers: auth(token) });
  if (!r.ok && r.status !== 204) return err(r);
}
// add_member uses QUERY params (not a body) per the backend signature.
export async function addGroupMember(token: string, groupId: string, userId: string, role: MemberRole = 'member'): Promise<Group> {
  const r = await fetch(`${API_URL}/groups/${groupId}/members?userId=${encodeURIComponent(userId)}&role=${role}`, { method: 'POST', headers: auth(token) });
  if (!r.ok) return err(r);
  return r.json();
}
export async function removeGroupMember(token: string, groupId: string, userId: string): Promise<void> {
  const r = await fetch(`${API_URL}/groups/${groupId}/members/${encodeURIComponent(userId)}`, { method: 'DELETE', headers: auth(token) });
  if (!r.ok && r.status !== 204) return err(r);
}
export async function listGroupTrails(token: string, groupId: string): Promise<Trail[]> {
  const r = await fetch(`${API_URL}/groups/${groupId}/trails`, { headers: auth(token) });
  if (!r.ok) return err(r);
  return r.json();
}

// ── invites ──
export async function createInvite(token: string, groupId: string, target: string, role: MemberRole = 'member'): Promise<GroupInvite> {
  // target = email OR tail tag ('@nick' / 'nick') — the backend resolves tags
  const body = target.startsWith('@')
    ? { tailTag: target.slice(1), role }
    : { email: target, role };
  const r = await fetch(`${API_URL}/groups/${groupId}/invites`, { method: 'POST', headers: j(token), body: JSON.stringify(body) });
  if (!r.ok) return err(r);
  return r.json();
}
export async function listGroupInvites(token: string, groupId: string): Promise<GroupInvite[]> {
  const r = await fetch(`${API_URL}/groups/${groupId}/invites`, { headers: auth(token) });
  if (!r.ok) return err(r);
  return r.json();
}
export async function revokeInvite(token: string, groupId: string, inviteId: string): Promise<void> {
  const r = await fetch(`${API_URL}/groups/${groupId}/invites/${encodeURIComponent(inviteId)}`, { method: 'DELETE', headers: auth(token) });
  if (!r.ok && r.status !== 204) return err(r);
}
export async function listMyInvites(token: string): Promise<GroupInvite[]> {
  const r = await fetch(`${API_URL}/me/invites`, { headers: auth(token) });
  if (!r.ok) return err(r);
  return r.json();
}
export async function acceptInvite(token: string, code: string): Promise<Group> {
  const r = await fetch(`${API_URL}/invites/${encodeURIComponent(code)}/accept`, { method: 'POST', headers: auth(token) });
  if (!r.ok) return err(r);
  return r.json(); // the joined group
}

// ── dog co-ownership ──
export async function listDogOwners(token: string, dogId: string): Promise<DogOwner[]> {
  const r = await fetch(`${API_URL}/dog/${encodeURIComponent(dogId)}/owners`, { headers: auth(token) });
  if (!r.ok) return err(r);
  return r.json();
}
export async function addDogOwner(token: string, dogId: string, userId: string): Promise<DogOwner[]> {
  const r = await fetch(`${API_URL}/dog/${encodeURIComponent(dogId)}/owners`, { method: 'POST', headers: j(token), body: JSON.stringify({ userId }) });
  if (!r.ok) return err(r);
  return r.json();
}
export async function removeDogOwner(token: string, dogId: string, userId: string): Promise<void> {
  const r = await fetch(`${API_URL}/dog/${encodeURIComponent(dogId)}/owners/${encodeURIComponent(userId)}`, { method: 'DELETE', headers: auth(token) });
  if (!r.ok && r.status !== 204) return err(r);
}

// ── trail visibility ──
export async function setTrailVisibility(token: string, trailId: string, visibility: Visibility, sharedGroupId?: string | null) {
  const r = await fetch(`${API_URL}/trail/${encodeURIComponent(trailId)}/visibility`, {
    method: 'PUT', headers: j(token), body: JSON.stringify({ visibility, sharedGroupId: sharedGroupId ?? null }),
  });
  if (!r.ok) return err(r);
  return r.json();
}
export async function getTrailVisibility(token: string, trailId: string): Promise<{ visibility: Visibility; sharedGroupId: string | null }> {
  const r = await fetch(`${API_URL}/trail/${encodeURIComponent(trailId)}/visibility`, { headers: auth(token) });
  if (!r.ok) return err(r);
  return r.json();
}
/** Typeahead for inviting: users matching by tail tag / email / name.
 *  Excludes current members and pending invitees of that group. */
export async function fetchInviteSuggestions(token: string, groupId: string, query: string): Promise<{ id: string; name: string | null; tail_tag: string; email: string; image?: string | null }[]> {
  const r = await fetch(`${API_URL}/groups/${groupId}/invite-suggestions?q=${encodeURIComponent(query)}`, { headers: auth(token) });
  if (!r.ok) return [];
  return r.json();
}
